import { useMemo, useState } from 'react';
import { PageHero } from '@components/common';
import { getKnowledgeCard } from '@data/knowledgeCards';
import { Search } from 'lucide-react';
import { CALENDLY_URL } from '@constants';

interface GlossaryTerm {
  term: string;
  definition: string;
}

const glossaryTerms: GlossaryTerm[] = [
  {
    term: 'APT (Advanced Persistent Threat)',
    definition:
      'A sophisticated, long-term cyberattack in which an intruder gains access to a network and remains undetected for an extended period, often with state-sponsored backing.',
  },
  {
    term: 'Attack Surface',
    definition:
      'The total number of points where an unauthorised user could try to enter or extract data from an environment. Includes devices, applications, cloud services, and people.',
  },
  {
    term: 'Blue Team',
    definition:
      'The defensive security group responsible for protecting an organisation against attacks and improving security posture over time.',
  },
  {
    term: 'Board Reporting',
    definition:
      'The practice of translating technical security risks, metrics, and incidents into business language so that non-executive directors can understand and act on them.',
  },
  {
    term: 'Business Continuity',
    definition:
      'The planning and preparation an organisation undertakes to ensure that critical business functions can continue during and after a disaster or disruption.',
  },
  {
    term: 'CISO (Chief Information Security Officer)',
    definition:
      "The senior executive responsible for establishing and maintaining an organisation's security strategy, policies, and risk management programme.",
  },
  {
    term: 'Compliance',
    definition:
      "Adherence to laws, regulations, and standards relevant to an organisation's operations. In cybersecurity, common frameworks include GDPR, ISO 27001, SOC 2, NIS2, and DORA.",
  },
  {
    term: 'CTEM (Continuous Threat Exposure Management)',
    definition:
      "A Gartner-defined approach that continuously evaluates and reduces an organisation's attack surface by prioritising exposures based on exploitability and business impact.",
  },
  {
    term: 'CVE (Common Vulnerabilities and Exposures)',
    definition:
      'A publicly disclosed cybersecurity vulnerability that has been assigned a unique identifier to enable consistent tracking and discussion.',
  },
  {
    term: 'CVSS (Common Vulnerability Scoring System)',
    definition:
      'An open framework for rating the severity of security vulnerabilities on a scale from 0.0 (low) to 10.0 (critical).',
  },
  {
    term: 'Cyber Insurance',
    definition:
      'An insurance product designed to help organisations mitigate risk exposure by offsetting costs involved with recovery from a cyber-related security breach or similar events.',
  },
  {
    term: 'Data Classification',
    definition:
      'The process of organising data into categories based on its sensitivity, value, and regulatory requirements so that appropriate security controls can be applied.',
  },
  {
    term: 'DLP (Data Loss Prevention)',
    definition:
      'A set of tools and processes used to ensure that sensitive data is not lost, misused, or accessed by unauthorised users.',
  },
  {
    term: 'EDR (Endpoint Detection and Response)',
    definition:
      'A cybersecurity solution that continuously monitors endpoints to detect and investigate suspicious activity in real time.',
  },
  {
    term: 'fCISO (Fractional CISO)',
    definition:
      'An experienced cybersecurity executive who provides strategic security leadership to organisations on a part-time or retainer basis.',
  },
  {
    term: 'IAM (Identity and Access Management)',
    definition:
      'The framework of policies and technologies that ensures the right individuals access the right resources at the right times for the right reasons.',
  },
  {
    term: 'Incident Response',
    definition:
      'The organised approach to addressing and managing the aftermath of a security breach or cyberattack, with the goal of limiting damage and reducing recovery time.',
  },
  {
    term: 'KEV (Known Exploited Vulnerabilities)',
    definition:
      'A catalogue maintained by CISA and other bodies of vulnerabilities that have been observed being actively exploited in the wild.',
  },
  {
    term: 'Living off the Land',
    definition:
      'A technique where attackers use legitimate tools and software already present on a target system to carry out malicious activities, making detection more difficult.',
  },
  {
    term: 'Malware',
    definition:
      'Any software intentionally designed to cause disruption to a computer, server, client, or computer network, leak private information, or gain unauthorised access.',
  },
  {
    term: 'MDR (Managed Detection and Response)',
    definition:
      'An outsourced security service that combines technology and human expertise to detect, investigate, and respond to threats in real time.',
  },
  {
    term: 'MFA (Multi-Factor Authentication)',
    definition:
      'A security mechanism that requires users to provide two or more verification factors to gain access to a resource, making stolen passwords alone insufficient.',
  },
  {
    term: 'MTTD (Mean Time to Detect)',
    definition:
      'The average time it takes for an organisation to identify a security incident from the moment it occurs.',
  },
  {
    term: 'MTTR (Mean Time to Respond / Recover)',
    definition:
      'The average time it takes to contain, remediate, and recover from a security incident once it has been detected.',
  },
  {
    term: 'N-day',
    definition:
      'A vulnerability that has been publicly disclosed and for which a patch is available, but remains exploitable on unpatched systems.',
  },
  {
    term: 'PAM (Privileged Access Management)',
    definition:
      'A security discipline that manages, monitors, and controls privileged access to critical systems and sensitive data.',
  },
  {
    term: 'Penetration Testing',
    definition:
      'A simulated cyberattack against your computer system to check for exploitable vulnerabilities, often conducted by external specialists.',
  },
  {
    term: 'Phishing',
    definition:
      'A social engineering attack in which an attacker sends fraudulent communications that appear to come from a reputable source, usually to steal credentials or deploy malware.',
  },
  {
    term: 'Purple Team',
    definition:
      "A collaborative exercise that combines red team (offensive) and blue team (defensive) activities to improve an organisation's security posture through shared learning.",
  },
  {
    term: 'Ransomware',
    definition:
      'A type of malicious software designed to block access to a computer system or data until a sum of money is paid.',
  },
  {
    term: 'Red Team',
    definition:
      "A group of security professionals authorised to emulate real-world attackers in order to test and improve an organisation's defences.",
  },
  {
    term: 'Risk Assessment',
    definition:
      "The process of identifying, analysing, and evaluating risks to an organisation's assets, operations, and reputation.",
  },
  {
    term: 'RPO (Recovery Point Objective)',
    definition:
      'The maximum acceptable amount of data loss measured in time. It determines how frequently backups must be taken to meet business continuity requirements.',
  },
  {
    term: 'RTO (Recovery Time Objective)',
    definition:
      'The maximum acceptable time that a system or service can be down after a disruption before unacceptable consequences occur.',
  },
  {
    term: 'Security Culture',
    definition:
      'The shared values, behaviours, and practices that determine how seriously an organisation and its employees take security in their day-to-day work.',
  },
  {
    term: 'Security Posture',
    definition:
      "The overall strength of an organisation's cybersecurity programme, including its policies, controls, and readiness to defend against threats.",
  },
  {
    term: 'Shadow AI',
    definition:
      'The unauthorised or unmonitored use of AI tools within an organisation, often by employees without IT or security oversight.',
  },
  {
    term: 'Shadow IT',
    definition:
      'The use of information technology systems, devices, software, applications, and services without explicit IT department approval.',
  },
  {
    term: 'SIEM (Security Information and Event Management)',
    definition:
      'A solution that aggregates and analyses security data from across an organisation to detect threats, support investigations, and enable compliance reporting.',
  },
  {
    term: 'SOAR (Security Orchestration, Automation and Response)',
    definition:
      'A stack of software solutions that allow organisations to collect data about security threats and respond to security events with minimal human intervention.',
  },
  {
    term: 'Social Engineering',
    definition:
      'The manipulation of people into performing actions or divulging confidential information, typically through deception.',
  },
  {
    term: 'Supply Chain Security',
    definition:
      "The practice of securing the networks, systems, and vendors that contribute to an organisation's products and services.",
  },
  {
    term: 'Tabletop Exercise',
    definition:
      'A discussion-based simulation where key stakeholders walk through a hypothetical security incident or crisis scenario to test plans, identify gaps, and improve response readiness.',
  },
  {
    term: 'Technical Debt',
    definition:
      'The implied cost of additional rework caused by choosing an easy solution now instead of using a better approach that would take longer.',
  },
  {
    term: 'Third-Party Risk',
    definition:
      'The potential threat presented to an organisation by external parties such as vendors, suppliers, contractors, and partners who have access to its systems or data.',
  },
  {
    term: 'Threat Actor',
    definition:
      'Any individual or group that poses a cybersecurity threat, including nation-states, criminal organisations, hacktivists, and insiders.',
  },
  {
    term: 'Threat Intelligence',
    definition:
      'Evidence-based knowledge about existing or emerging threats to assets, including context, mechanisms, indicators, and actionable advice.',
  },
  {
    term: 'Threat Landscape',
    definition:
      'The overall picture of the threats currently facing an organisation or industry, including the types of attackers, methods, and vulnerabilities in play.',
  },
  {
    term: 'vCISO (Virtual CISO)',
    definition:
      'A CISO who delivers security leadership remotely, often through a managed service or consultancy arrangement.',
  },
  {
    term: 'Vulnerability Management',
    definition:
      'The continuous process of identifying, evaluating, remediating, and reporting on security vulnerabilities in systems and software.',
  },
  {
    term: 'XDR (Extended Detection and Response)',
    definition:
      'A security platform that unifies detection and response across multiple data sources - such as endpoints, networks, email, and cloud - to improve threat visibility and response speed.',
  },
  {
    term: 'Zero Day',
    definition:
      'A software vulnerability that is unknown to the vendor and has no available patch at the time it is discovered or exploited.',
  },
  {
    term: 'Zero Trust',
    definition:
      'A security model that assumes no user or device should be trusted by default, regardless of whether they are inside or outside the network perimeter. Access is granted strictly on a need-to-know, verified basis.',
  },
];

export default function GlossaryPage() {
  const [query, setQuery] = useState('');

  const glossaryStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: 'CISO Glossary | The CISO Network',
    description:
      'Plain-English definitions of the terms, acronyms, and concepts every security leader and board member should know.',
    url: 'https://thecisonetwork.com/knowledge-hub/glossary',
    inLanguage: 'en-GB',
    datePublished: '2026-04-02T00:00:00+01:00',
    dateModified: '2026-04-02T00:00:00+01:00',
    author: {
      '@type': 'Person',
      name: 'Richard Midwinter',
      url: 'https://www.linkedin.com/in/r-i-c-h-a-r-d',
      image: 'https://thecisonetwork.com/images/team/cto.jpg',
      jobTitle: 'CTO',
      worksFor: {
        '@type': 'Organization',
        name: 'The CISO Network',
        url: 'https://thecisonetwork.com',
      },
    },
    publisher: {
      '@type': 'Organization',
      name: 'The CISO Network',
      url: 'https://thecisonetwork.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://thecisonetwork.com/images/logo.png',
      },
    },
    hasDefinedTerm: glossaryTerms.map(item => ({
      '@type': 'DefinedTerm',
      name: item.term,
      description: item.definition,
    })),
  };

  const filteredTerms = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return glossaryTerms;
    return glossaryTerms.filter(
      t => t.term.toLowerCase().includes(q) || t.definition.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(glossaryStructuredData) }}
      />
      <PageHero
        title="CISO Glossary"
        subtitle="Plain-English definitions of the terms, acronyms, and concepts every security leader and board member should know."
        eyebrow="Knowledge Hub"
        backgroundImage={getKnowledgeCard('glossary')?.image}
        backgroundAlt={getKnowledgeCard('glossary')?.imageAlt}
        seoTitle="CISO Glossary | The CISO Network"
        seoDescription="A plain-English glossary of cybersecurity terms, acronyms, and concepts for security leaders, board members, and growing businesses."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search terms..."
                className="w-full pl-12 pr-4 py-3 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal transition-colors"
              />
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            {filteredTerms.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">
                No terms match your search. Try a different keyword.
              </p>
            ) : (
              <dl className="space-y-8">
                {filteredTerms.map(item => (
                  <div
                    key={item.term}
                    className="p-6 rounded-xl bg-background border border-border"
                  >
                    <dt className="text-lg font-heading font-semibold text-foreground mb-2">
                      {item.term}
                    </dt>
                    <dd className="text-muted-foreground leading-relaxed">{item.definition}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Need Help Making Sense of It All?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            If you are struggling to translate these terms into action, our fractional CISOs can
            help cut through the jargon and build a security programme that fits your business.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
